import { Component, OnInit, OnDestroy, ViewContainerRef, ViewChild, ComponentFactoryResolver } from '@angular/core';
import { ActivatedRoute, Params, Router, NavigationEnd, RouterEvent } from '@angular/router';

import { Subscription } from 'rxjs';
import { filter, map, mergeMap, skip } from 'rxjs/internal/operators';
import { TranslateService } from '@ngx-translate/core';

import { AuthStore } from './auth/auth.store';
import { AuthService } from './auth/auth.service';
import { AUTHORIZATION_PARAMETER } from './constants';
import { SpinnerService } from './service/spinner.service';
import { PaylocMenuComponent } from './payloc/payloc-menu/payloc-menu.component';
import { PageScrollConfig } from 'ngx-page-scroll';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {

  public showSpinner = false;
  private routerEventsSubscription: Subscription;
  private spinnerSubscription: Subscription;

  componentRef: any;
  title: string;
  @ViewChild('dynamicComponent', { read: ViewContainerRef }) entry: ViewContainerRef;

  constructor(
    private authStore: AuthStore,
    private authService: AuthService,
    private translate: TranslateService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private spinnerService: SpinnerService,
    private resolver: ComponentFactoryResolver
  ) {
    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('en');

    PageScrollConfig.defaultDuration = 100;
  }

  createDynamicComponent(component: any) {
    this.entry.clear();
    const factory = this.resolver.resolveComponentFactory(component);
    const componentRef = this.entry.createComponent(factory);
  }

  destroyDynamicComponent() {
    this.componentRef.destroy();
  }

  ngOnInit() {
    this.routerEventsSubscription = this.router.events.subscribe((event: NavigationEnd) => {
      window.scroll(0, 0);
    });

    this.routerEventsSubscription = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route: ActivatedRoute) => {
        while (route.firstChild) {
          route = route.firstChild;
        }

        return route;
      }),
      filter((route: ActivatedRoute) => route.outlet === 'primary'),
      mergeMap((route: ActivatedRoute) => route.data)
    ).subscribe((event: RouterEvent) => {
      const routeTitle: string = event['title'] || 'Home';
      const routeMenu: any = event['menuComponent'] || PaylocMenuComponent; // change this for a defult menu
      const pageTitle = `${routeTitle} | Onyx Customer Service`;
      // TODO: implement title using routeTitle value
      this.createDynamicComponent(routeMenu);
    });

    this.spinnerSubscription = this.spinnerService.showSpinner.subscribe(
      (showSpinner: boolean) => {
        this.showSpinner = showSpinner;
      }
    );

    this.trimAuthorizationParam();
  }

  trimAuthorizationParam() {
    const paramsCopy = {};

    this.activatedRoute.queryParams
      .pipe(skip(1))
      .subscribe((params: Params) => {
        const token = params[AUTHORIZATION_PARAMETER];
        if (token) {
          this.authService.login(token);
        }

        for (const key in params) {
          if (params.hasOwnProperty(key)) {
            if (key !== AUTHORIZATION_PARAMETER) {
              paramsCopy[key] = params[key];
            }
          }
        }

        this.router.navigate(['.'], { queryParams: paramsCopy });
      });
  }

  public ngOnDestroy(): void {
    // NOTE: Unsubscribing here is not necessary because the Angular framework handles this for us; however,
    // we implement this out of convention to set an example for services that need manual memory management
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }

    if (this.spinnerSubscription) {
      this.spinnerSubscription.unsubscribe();
    }

    this.destroyDynamicComponent();
  }
}
